<template>
  <div
    class="w-full flex items-center px-4"
    :class="{ 'bg-primary5': isSelected, '!bg-primary4': allowRowHighlight && (isContextMenuOpen || isDraggedOver) }"
    :style="{ borderBottom: `1px solid ${themeVars.primary6}` }"
  >
    <div class="flex items-center">
      <div class="my-3">
        <CFileThumbnail :item="item as RenderableFile" />
      </div>
    </div>
    <div class="flex flex-col justify-center gap-1 pa-3 truncate w-full">
      <CTableInput
        :modelValue="name ?? item.name"
        :readonly="isReadonly()"
        :search="highlightText"
        :state="isEditingCurrentItem(item?.id, 'name') ? 'edit' : 'view'"
        valueClass="font-700 truncate font-size-[18px]"
        @cancel="clearInlineEditingItem()"
        @edit="onEdit(item.id, 'name')"
        @update:modelValue="onUpdateName(item.id.toString(), item.type, $event)"
      />
      <div
        class="truncate font-size-[12px]"
        :style="{ color: isContextMenuOpen || isDraggedOver ? themeVars.text2 : themeVars.text3 }"
      >
        <span v-if="item.type === 'smart_folder'">
          <CIcon class="mr-1" icon="folder-bookmark" />
          <span v-if="doNotShowFolderDetails">{{ t('canvasUI.common.folder') }}</span>
          <span v-else>{{ getFolderDescription(getItemAsFolder(item)) }}</span>
        </span>

        <div v-else-if="item.folder?.name" class="flex gap-2 items-center">
          <div class="flex gap-1 items-center">
            <div>{{ t('canvasUI.files.in') }}</div>
            <div class="color-text3 font-bold hover:underline" @click.stop="openFolder(item.folder.id)">
              {{ item.folder?.name }}
            </div>
            <span class="inline-block h-1 w-1 bg-text3 rounded-full"></span>
            <component :is="renderContentType(item.content_type)" />
          </div>
        </div>

        <div v-else-if="showItemType" class="flex gap-2 items-center">
          <component :is="renderContentType(item.content_type)" />
        </div>
      </div>
      <div v-if="item.type !== 'smart_folder'" class="flex items-center gap-2 mb-1">
        <CTag
          size="small"
          :style="{
            ...getStatusStyle(),
            border: 'none',
            fontSize: '10px',
            letterSpacing: '0.5px',
            textTransform: 'uppercase',
            '--n-padding': '4px 8px',
          }"
        >
          {{
            getIsDraft()
              ? t('canvasUI.components.templates.CTemplateManagementStatusCell.status.draft')
              : t('canvasUI.components.templates.CTemplateManagementStatusCell.status.ready')
          }}
        </CTag>
      </div>
      <div class="flex flex-wrap gap-2 items-center">
        <CDynamicIndicator
          v-for="metadataProperty of pitcherSettings.metadata_highlight_properties ?? []"
          :key="metadataProperty.key"
          :item="item"
          :metadataProperty="metadataProperty"
        />
      </div>
      <CTableTag
        v-if="item?.tags"
        display-as-text
        :maxTagsToShow="0"
        :modelValue="item?.tags"
        :readonly="isReadonly() || !!item.page"
        :search="highlightText"
        :state="isEditingCurrentItem(item?.id, 'tags') ? 'edit' : 'view'"
        :suggested-tags-fetcher="(search) => fetchSuggestedInstanceTags(search, { type: ['files'] })"
        :tagsClass="'cursor-pointer'"
        @cancel="clearInlineEditingItem()"
        @edit="onEdit(item.id, 'tags')"
        @update:modelValue="onUpdateTags(item.id.toString(), $event)"
      />
      <div class="flex flex-wrap gap-2">
        <CContentError v-if="item?.access_type === 'restricted'" only-tag restricted />
        <CInfoBadge
          v-if="item?.permissions?.can_share === false"
          :background-color="themeVars.error2"
          :color="themeVars.error"
          fa-type="fa-kit"
          icon="solid-share-from-square-slash"
          :text="t('canvasUI.files.notShareable')"
        />
        <CPopularityAndEngagementExtended
          v-if="Object.keys(popularityDict)?.length"
          class="gap-2 mr-2"
          :engagement="launchDarkly.show_popularity_icons ? popularityDict[item.id]?.engagement : undefined"
          :is-recommended="!!recommendedSectionIds.includes(item.id?.toString())"
          :popularity="launchDarkly.show_popularity_icons ? popularityDict[item.id]?.popularity : undefined"
          :views="launchDarkly.show_popularity_icons ? (popularityDict[item.id]?.views ?? 0) : 0"
        />
      </div>
    </div>

    <div class="flex justify-end h-full items-center gap-1 cursor-auto py-3" @click.stop>
      <slot name="actions"></slot>
    </div>
  </div>
</template>
<script setup lang="ts">
import { computed, inject, type ComputedRef } from 'vue'
import { useI18n } from 'vue-i18n'

import CDynamicIndicator from '@lib/components/CDynamicIndicator/CDynamicIndicator.vue'
import CFileThumbnail from '@lib/components/CFileThumbnail/CFileThumbnail.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import CInfoBadge from '@lib/components/CInfoBadge/CInfoBadge.vue'
import CPopularityAndEngagementExtended from '@lib/components/CPopularityAndEngagementIcons/CPopularityAndEngagementExtended.vue'
import CTableInput from '@lib/components/CTableInput/CTableInput.vue'
import CTableTag from '@lib/components/CTableTag/CTableTag.vue'
import CTag from '@lib/components/CTag/CTag.vue'

import useApiStore from '@browser/stores/api'
import useAppStore from '@browser/stores/app'
import useFileDisplayHelpers from '@lib/composables/useFileDisplayHelpers'
import useRecommendations from '@canvas-builder/composables/useRecommendations'
import useSectionPopularity from '@canvas-builder/composables/useSectionPopularity'
import useSuggestedTags from '@lib/composables/suggestedTags.use'
import useThemeVars from '@lib/composables/useThemeVars'
import useFileSmartFolderStore from '@browser/stores/fileSmartFolderStore'
import { openFolder } from '@browser/util/file'

import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'
import type { RenderableFile, ViewCompactFolder } from '@lib/types/files'
import { type OrganizationSettings } from '@lib/types/organizationSettings.types'
import { type InstanceSettings } from '@lib/types/instanceSettings.types'
import CContentError from '@lib/components/CContentError/CContentError.vue'

const props = withDefaults(
  defineProps<{
    filters: ComputedRef<Record<string, any>>
    allowRowHighlight?: boolean
    highlightText?: string
    doNotShowFolderDetails?: boolean
    isContextMenuOpen?: boolean
    isDraggedOver?: boolean
    isSelected?: boolean
    item: { [key: string]: any; id: number | string }
    name?: string
    showItemType?: boolean
  }>(),
  {
    allowRowHighlight: true,
    highlightText: '',
    doNotShowFolderDetails: false,
    isContextMenuOpen: false,
    isDraggedOver: false,
    isSelected: false,
    showItemType: true,
  },
)

const { t } = useI18n()
const { state: apiState } = useApiStore()
const { appName, clearInlineEditingItem, inlineEditingItem } = useAppStore()
const { getFolderDescription, renderContentType } = useFileDisplayHelpers()
const { popularityDict } = useSectionPopularity(props.filters)
const { recommendedSectionIds } = useRecommendations()
const { fetchSuggestedInstanceTags } = useSuggestedTags()
const themeVars = useThemeVars()
const store = useFileSmartFolderStore()

const pitcherSettings = inject<ComputedRef<OrganizationSettings & InstanceSettings>>(
  'pitcherSettings',
  computed(() => ({})),
)

const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)

function getItemAsFolder(item: any) {
  return item as ViewCompactFolder
}

function getIsDraft() {
  return props.item.is_draft ?? false
}

function getStatusStyle() {
  return getIsDraft()
    ? {
        color: themeVars.value.text,
        backgroundColor: themeVars.value.primary6,
      }
    : {
        color: themeVars.value.base,
        backgroundColor: themeVars.value.success,
      }
}

function isEditingCurrentItem(id: string | number, prop: 'name' | 'tags') {
  return inlineEditingItem.value?.id === id && inlineEditingItem.value?.prop === prop
}

function isReadonly() {
  return appName.value === 'impact' || !!inlineEditingItem.value?.id || store.state.viewMode === 'folders'
}

function onEdit(id: string | number, prop: 'name' | 'tags') {
  inlineEditingItem.value = { id, prop }
}

async function onUpdateName(id: string, type: 'file' | 'folder', newValue: string) {
  switch (type) {
    case 'folder':
      await apiState.folderUpdater?.({ id, name: newValue })
      break
    case 'file': {
      const updatedFile = await apiState.fileUpdater?.({ id, name: newValue })
      if (updatedFile && store.state.matchingFiles) {
        store.actions.setMatchingFiles(
          store.state.matchingFiles.map((file) =>
            file.id === updatedFile.id ? { ...file, name: updatedFile.name ?? file.name } : file,
          ),
        )
      }
      break
    }
  }
  clearInlineEditingItem()
}

async function onUpdateTags(id: string, newValue: string[]) {
  const updatedFile = await apiState.fileUpdater?.({ id, tags: newValue })
  if (updatedFile && store.state.matchingFiles) {
    store.actions.setMatchingFiles(
      store.state.matchingFiles.map((file) =>
        file.id === updatedFile.id ? { ...file, tags: updatedFile.tags } : file,
      ),
    )
  }
  clearInlineEditingItem()
}
</script>
